import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// A row's actions collapsed behind one "⋯" button.
//
// The catalog table gave four buttons per row their own permanent real estate —
// on a 1024px screen that column pushed Unit $ under the sticky edge and left
// the prices themselves showing as "2..". Actions are the least-read thing in a
// row and the most expensive to display, so they live here instead.
//
// Rendered through a portal into document.body: the table body is a scroll
// container with `overflow: auto`, and a menu positioned inside it would be
// clipped at the row that opened it. Fixed positioning off the button's
// bounding rect is the same trick CountInterface's PortalCardMenu uses, with
// keyboard support added — this menu is reachable by Tab, so it has to be
// operable without a mouse.
//
// `items` is an array of { key, label, icon, onSelect, disabled, danger, title }.
// A null/false entry is skipped, so callers can inline conditionals.

const MENU_WIDTH = 200;
const VIEWPORT_MARGIN = 8;

const RowActionMenu = ({
  label = 'Row actions',
  items = [],
  buttonClassName = 'finder-kebab-btn',
  menuLabel = null,
}) => {
  const visibleItems = items.filter(Boolean);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const itemRefs = useRef([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [position, setPosition] = useState(null);

  const close = useCallback((refocus = true) => {
    setOpen(false);
    setActiveIndex(-1);
    if (refocus && buttonRef.current) buttonRef.current.focus();
  }, []);

  // Measure after paint so the menu's real height decides whether it opens
  // upward. Guessing a fixed height (the older pattern used 220px) drops the
  // menu off-screen for the last rows of a long table.
  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const menuHeight = menuRef.current?.offsetHeight || (visibleItems.length * 38 + 8);
    const spaceBelow = window.innerHeight - rect.bottom;
    const openAbove = spaceBelow < menuHeight + VIEWPORT_MARGIN && rect.top > menuHeight + VIEWPORT_MARGIN;
    // Right-align to the button, but never let the menu run off the left edge
    // on a phone where the button sits near x=0.
    const right = Math.max(VIEWPORT_MARGIN, Math.min(
      window.innerWidth - rect.right,
      window.innerWidth - MENU_WIDTH - VIEWPORT_MARGIN
    ));
    setPosition(openAbove
      ? { bottom: window.innerHeight - rect.top + 4, top: 'auto', right }
      : { top: rect.bottom + 4, bottom: 'auto', right });
  }, [open, visibleItems.length]);

  // Any scroll or resize invalidates the fixed position we just measured, so
  // the menu closes rather than hovering over the wrong row.
  useEffect(() => {
    if (!open) return undefined;
    const dismiss = () => close(false);
    const onPointerDown = (event) => {
      if (menuRef.current?.contains(event.target)) return;
      if (buttonRef.current?.contains(event.target)) return;
      close(false);
    };
    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open, close]);

  useEffect(() => {
    if (!open || activeIndex < 0) return;
    itemRefs.current[activeIndex]?.focus();
  }, [open, activeIndex]);

  const firstEnabled = () => visibleItems.findIndex((item) => !item.disabled);
  const lastEnabled = () => {
    for (let i = visibleItems.length - 1; i >= 0; i -= 1) if (!visibleItems[i].disabled) return i;
    return -1;
  };

  const step = (from, delta) => {
    if (visibleItems.length === 0) return -1;
    let next = from;
    for (let hops = 0; hops < visibleItems.length; hops += 1) {
      next = (next + delta + visibleItems.length) % visibleItems.length;
      if (!visibleItems[next].disabled) return next;
    }
    return -1;
  };

  const onButtonKeyDown = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex(firstEnabled());
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex(lastEnabled());
    }
  };

  const onMenuKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((current) => step(current, 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((current) => step(current, -1));
    } else if (event.key === 'Home') {
      event.preventDefault();
      setActiveIndex(firstEnabled());
    } else if (event.key === 'End') {
      event.preventDefault();
      setActiveIndex(lastEnabled());
    } else if (event.key === 'Tab') {
      close(false);
    }
  };

  const choose = (item) => {
    if (item.disabled) return;
    close();
    item.onSelect?.();
  };

  if (visibleItems.length === 0) return null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={buttonClassName}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={label}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((wasOpen) => !wasOpen);
          setActiveIndex(-1);
        }}
        onKeyDown={onButtonKeyDown}
      >
        <span aria-hidden="true">⋯</span>
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          className="finder-kebab-menu"
          role="menu"
          aria-label={menuLabel || label}
          tabIndex={-1}
          style={{
            position: 'fixed',
            top: position?.top ?? -9999,
            bottom: position?.bottom ?? 'auto',
            right: position?.right ?? 0,
            width: MENU_WIDTH,
            // Hidden until measured, so the menu never flashes at the top-left
            // corner on the frame before useLayoutEffect runs.
            visibility: position ? 'visible' : 'hidden',
          }}
          onKeyDown={onMenuKeyDown}
          onClick={(event) => event.stopPropagation()}
        >
          {visibleItems.map((item, index) => (
            <button
              key={item.key || item.label}
              ref={(el) => { itemRefs.current[index] = el; }}
              type="button"
              role="menuitem"
              className={`finder-kebab-item${item.danger ? ' finder-kebab-item-danger' : ''}`}
              disabled={item.disabled}
              title={item.title || undefined}
              tabIndex={index === activeIndex ? 0 : -1}
              onClick={() => choose(item)}
            >
              <span className="finder-kebab-icon" aria-hidden="true">{item.icon || ''}</span>
              <span className="finder-kebab-label">{item.label}</span>
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
};

export default RowActionMenu;
